/**
 * Aave reserve-index sanity guard.
 *
 * `liquidityIndex` and `variableBorrowIndex` are RAY-scaled accumulators that
 * start at exactly 1 RAY when a reserve is initialised and only ever grow, at
 * interest-rate speed. Four years of Aave V3 mainnet at ~5 % has moved the
 * oldest indices to roughly 1.2 — so a legitimate index is never remotely near
 * the ceiling below.
 *
 * The guard exists because that invariant is exactly what gets broken when an
 * Aave fork is attacked. dTRINITY's Ethereum dLEND deployment was drained on
 * 2026-03-17 by inflating a six-day-old cbBTC reserve's `liquidityIndex` from
 * ~1.0 RAY to ~6.2 MILLION RAY, which turned 772 USDC into $4.8M of nominal
 * collateral. Every downstream consumer we have — health factors, borrow
 * capacity, the pair book, liquidation dashboards — multiplies scaled balances
 * by these indices and trusts the result implicitly, so a repeat anywhere in
 * the fork roster would propagate fabricated collateral through the whole
 * stack. Publishing NO row for such a reserve is strictly better than
 * publishing a fictitious one.
 *
 * The threshold is deliberately absurd (1000x) rather than tight: the point is
 * to catch a manipulation that overshoots by six orders of magnitude without
 * ever risking a false positive on a long-lived, high-rate market. An index of
 * zero is left alone — that is simply an uninitialised or unlisted reserve, not
 * an anomaly.
 */

const RAY = 10n ** 27n

/**
 * Upper bound on a plausible index, as a multiple of RAY. A reserve would need
 * to compound for well over a century at double-digit rates to reach it.
 */
export const MAX_PLAUSIBLE_INDEX_MULTIPLE = 1000n

/**
 * `false` when an index is impossibly large — i.e. manipulated. Zero/undefined
 * (an uninitialised reserve) and anything in normal range return `true`.
 */
export function isReserveIndexSane(
  index: bigint | string | undefined,
): boolean {
  if (index === undefined || index === null) return true
  let value: bigint
  try {
    value = BigInt(index)
  } catch {
    return true
  }
  if (value === 0n) return true
  return value <= RAY * MAX_PLAUSIBLE_INDEX_MULTIPLE
}

/**
 * Guard for a whole reserve. Logs once per offending reserve so a live
 * manipulation is visible in worker logs rather than silently swallowed.
 */
export function reserveIndicesAreSane(params: {
  chainId: string
  lender: string
  asset: string
  liquidityIndex: bigint | string | undefined
  variableBorrowIndex: bigint | string | undefined
}): boolean {
  const { chainId, lender, asset, liquidityIndex, variableBorrowIndex } = params
  const liquidityOk = isReserveIndexSane(liquidityIndex)
  const borrowOk = isReserveIndexSane(variableBorrowIndex)
  if (liquidityOk && borrowOk) return true
  console.error(
    `[reserve-index-sanity] dropping ${lender} ${asset} on chain ${chainId}: ` +
      `liquidityIndex=${liquidityIndex} variableBorrowIndex=${variableBorrowIndex} ` +
      `— implausible accumulator, refusing to publish derived balances`,
  )
  return false
}
